import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { Card } from 'components/Card';
import { useTrailer } from './trailerContext';
import { updateTrailer } from '../models';
import { getDisplayError } from '@utils/get-display-error';
import { Button } from 'components/button/Button';
import { InputField } from 'components/input/InputField';
import { InternalUser, UserComboboxField } from 'src/app/user/components/UserCombobox';
import { CheckboxField } from 'components/checkbox/CheckboxField';
import { SimpleSelectField } from 'components/select/SimpleSelectField';
import { captureException } from '@sentry/react';
import { TrailerTrackingPanel } from 'src/app/telemetry/components/TrailerTrackingPanel';
import { INTERNAL_TRAILER_TYPE_OPTIONS } from '../constants';

const updateTrailerSchema = Yup.object().shape({
  name: Yup.string().min(1, 'Vereist').required('Vereist'),
  licensePlate: Yup.string().min(1, 'Vereist').required('Vereist'),
  brand: Yup.string().min(1, 'Vereist').required('Vereist'),
  serialNumber: Yup.string().min(1, 'Vereist').required('Vereist'),
  registrationDate: Yup.string().required('Vereist'),
  insuranceExpirationDate: Yup.string().when('isRental', {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.required('Vereist'),
  }),
  inspectionExpirationDate: Yup.string().when('isRental', {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.required('Vereist'),
  }),
  trailerType: Yup.object().required('Vereist'),
});

export const UpdateTrailerForm = () => {
  const { trailer, refreshData } = useTrailer();

  const initialValues = useMemo(() => {
    return {
      name: trailer.name,
      licensePlate: trailer.licensePlate,
      brand: trailer.brand,
      serialNumber: trailer.serialNumber,
      registrationDate: trailer.registrationDate,
      insuranceExpirationDate: trailer.insuranceExpirationDate ?? '',
      inspectionExpirationDate: trailer.inspectionExpirationDate ?? '',
      responsible: trailer.responsible as InternalUser,
      trailerType:
        INTERNAL_TRAILER_TYPE_OPTIONS.find((option) => option.key === trailer.trailerType) ??
        INTERNAL_TRAILER_TYPE_OPTIONS[0],
      isRental: trailer.isRental ?? false,
      rentalCompany: trailer.rentalCompany ?? '',
    };
  }, [trailer]);

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={updateTrailerSchema}
        onSubmit={async (newValues) => {
          try {
            const { responsible, trailerType, ...patch }: Partial<typeof newValues> = diff(initialValues, newValues);
            const updateData = {
              responsibleId: responsible?.id,
              trailerType: trailerType?.key,
              ...patch,
            };

            if (Object.values(updateData).length) {
              await updateTrailer(trailer.id, updateData);
              await refreshData();
            }
            toast.success('Trailer aangepast');
          } catch (err: any) {
            captureException(err);
            toast.error('Kon trailer niet aanpassen: ' + getDisplayError(err));
          }
        }}
      >
        {({ handleSubmit, isSubmitting, values, setFieldValue }) => {
          return (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <Card title="Identificatie">
                <UserComboboxField labelText="Verantwoordelijke" name="responsible" />
                <InputField labelText="ID Code" type="text" name="name" />
                <InputField labelText="Nummerplaat" type="text" name="licensePlate" />
                <InputField labelText="Merk" type="text" name="brand" />
                <InputField labelText="Serienummer" type="text" name="serialNumber" />
                <InputField labelText="Datum eerste registratie" type="date" name="registrationDate" />
                <SimpleSelectField labelText="Type" name="trailerType" items={[...INTERNAL_TRAILER_TYPE_OPTIONS]} />
              </Card>

              <Card title="Huurtrailer">
                <CheckboxField labelText="Dit is een huurtrailer" name="isRental" />
                {values.isRental && <InputField labelText="Verhuurmaatschappij" type="text" name="rentalCompany" />}
              </Card>

              <Card title="Vervaldagen">
                <InputField labelText="Vervaldag verzekering" type="date" name="insuranceExpirationDate" />
                <InputField labelText="Vervaldag keuring" type="date" name="inspectionExpirationDate" />
              </Card>

              <div>
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={!!isSubmitting}
                  isLoading={isSubmitting}
                  iconLeft={<PlusIcon className="button-icon" />}
                >
                  Bewaar aanpassingen
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>

      {/* Under the form rather than in it: which unit reports this trailer's position is a link, not a
          field, and it is saved on its own. */}
      <div className="mt-4">
        <TrailerTrackingPanel trailer={trailer} />
      </div>
    </div>
  );
};
